import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { getProductSpotlightCards } from "@faire/retailer-visitor-shared/components/BrandProductSpotlight/utils";
// eslint-disable-next-line import/no-unassigned-import
import "@faire/retailer-visitor-shared/components/slick.css";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web--source/common/transform-images";
import { Button } from "@faire/web--source/slate/Button";
import { Arrow } from "@faire/web--source/slate/icons/Arrow";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackBrandLeadsBrandsPageSpotlightCarouselView } from "@faire/web-api--source/events/brandLeads/view/brandsPageSpotlightCarousel";
import times from "lodash/times";
import { observer } from "mobx-react";
import React, { useCallback, useState, useRef } from "react";
import Slider from "react-slick";
import styled from "styled-components";
var BrandProductSpotlightBase = function(props) {
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _props_initialSlide;
    var _useState = _sliced_to_array(useState((_props_initialSlide = props.initialSlide) !== null && _props_initialSlide !== void 0 ? _props_initialSlide : 0), 2), currentIndex = _useState[0], setCurrentIndex = _useState[1];
    var cardSliderRef = useRef(null);
    var _useViewport = useViewport(), isMobile = _useViewport.isMobile, isTabletAndAbove = _useViewport.isTabletAndAbove;
    var isSSR = useIsSSR();
    var onClickNextSlideHandler = useCallback(function(e) {
        var _cardSliderRef_current;
        e.stopPropagation();
        setCurrentIndex(currentIndex + 1);
        (_cardSliderRef_current = cardSliderRef.current) === null || _cardSliderRef_current === void 0 ? void 0 : _cardSliderRef_current.slickNext();
    }, [
        currentIndex
    ]);
    var onClickPrevSlideHandler = useCallback(function(e) {
        var _cardSliderRef_current;
        e.stopPropagation();
        (_cardSliderRef_current = cardSliderRef.current) === null || _cardSliderRef_current === void 0 ? void 0 : _cardSliderRef_current.slickPrev();
        setCurrentIndex(currentIndex - 1);
    }, [
        currentIndex
    ]);
    var onCardClickHandler = useCallback(function(index) {
        var _cardSliderRef_current;
        setCurrentIndex(index);
        (_cardSliderRef_current = cardSliderRef.current) === null || _cardSliderRef_current === void 0 ? void 0 : _cardSliderRef_current.slickGoTo(index);
    }, []);
    var handleSliderChangeHandler = useCallback(function(_, newIndex) {
        var _getProductSpotlightCards_currentIndex;
        setCurrentIndex(newIndex);
        var key = (_getProductSpotlightCards_currentIndex = getProductSpotlightCards(strictLocalize)[currentIndex]) === null || _getProductSpotlightCards_currentIndex === void 0 ? void 0 : _getProductSpotlightCards_currentIndex.eventKey;
        if (key) {
            trackBrandLeadsBrandsPageSpotlightCarouselView(key);
        }
    }, [
        currentIndex,
        strictLocalize
    ]);
    var viewRef = useDoActionWhenVisible(props.onView);
    var renderCardContentHandler = useCallback(function(card, index, isMobile) {
        return /*#__PURE__*/ _jsxs(Card, {
            $isSelected: index === currentIndex,
            children: [
                /*#__PURE__*/ _jsxs(ImageContainer, {
                    children: [
                        isMobile ? /*#__PURE__*/ _jsx(Spacer, {
                            size: "4x"
                        }) : null,
                        /*#__PURE__*/ _jsx("img", {
                            src: isMobile ? optimizeImageUrl({
                                url: card.image
                            }, isSSR, {
                                width: 317,
                                height: 181
                            }) : optimizeImageUrl({
                                url: card.image
                            }, isSSR, {
                                width: 410,
                                height: 258
                            }),
                            style: {
                                width: isMobile ? 317 : 410,
                                height: isMobile ? 181 : 258
                            }
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(TitlesContainer, {
                    children: [
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: "2x"
                        }),
                        /*#__PURE__*/ _jsx(CardTitle, {
                            variant: isMobile ? "subheadingSansMedium" : "subheadingSansRegular",
                            align: isMobile ? "left" : "center",
                            children: card.title
                        }),
                        isMobile ? null : /*#__PURE__*/ _jsx(Spacer, {
                            size: "1x"
                        }),
                        /*#__PURE__*/ _jsx(SubtitleContainer, {
                            align: isMobile ? "left" : "center",
                            children: card.subtitle
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: "2x"
                        })
                    ]
                })
            ]
        });
    }, [
        currentIndex,
        isSSR
    ]);
    var productSpotlightCards = getProductSpotlightCards(strictLocalize);
    var _props_title;
    return /*#__PURE__*/ _jsxs(OuterContainer, {
        ref: props.onView ? viewRef : undefined,
        $showCaseStudies: props.showCaseStudies,
        $isPersonalized: props.isPersonalized,
        children: [
            /*#__PURE__*/ _jsxs(Container, {
                children: [
                    /*#__PURE__*/ _jsx(Spacer, {
                        size: isMobile ? "4x" : "10x"
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: {
                            mobileAndAbove: "pageHeaderSerifRegular",
                            tabletAndAbove: "displayMSerifRegular",
                            desktopAndAbove: "displayLSerifRegular"
                        },
                        align: isMobile ? "left" : "center",
                        style: {
                            marginLeft: isMobile ? getSpacing("3x") : 0
                        },
                        children: (_props_title = props.title) !== null && _props_title !== void 0 ? _props_title : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "FxAXJ6",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "A simple, easy-to-use B2B platform"
                                }
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        size: isMobile ? "2x" : "9x"
                    }),
                    /*#__PURE__*/ _jsxs(SlideControlWrapper, {
                        children: [
                            isTabletAndAbove ? /*#__PURE__*/ _jsxs(SlideControlButtonsWrapper, {
                                children: [
                                    /*#__PURE__*/ _jsx(SlideControlButton, {
                                        variant: "secondary",
                                        onClick: function(e) {
                                            return onClickPrevSlideHandler(e);
                                        },
                                        children: /*#__PURE__*/ _jsx(Arrow, {
                                            width: "14px",
                                            height: "8px",
                                            direction: "left"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Spacer, {
                                        width: 450
                                    }),
                                    /*#__PURE__*/ _jsx(SlideControlButton, {
                                        variant: "secondary",
                                        onClick: function(e) {
                                            return onClickNextSlideHandler(e);
                                        },
                                        children: /*#__PURE__*/ _jsx(Arrow, {
                                            width: "14px",
                                            height: "8px",
                                            direction: "right"
                                        })
                                    })
                                ]
                            }) : null,
                            /*#__PURE__*/ _jsx(StyledSlider, {
                                ref: cardSliderRef,
                                arrows: false,
                                variableWidth: true,
                                beforeChange: handleSliderChangeHandler,
                                infinite: true,
                                centerMode: true,
                                children: productSpotlightCards.map(function(card, index) {
                                    return /*#__PURE__*/ _jsx("div", {
                                        children: /*#__PURE__*/ _jsx(CardContainer, {
                                            onClick: function() {
                                                return onCardClickHandler(index);
                                            },
                                            children: renderCardContentHandler(card, index, isMobile)
                                        })
                                    }, index);
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                size: isMobile ? "2x" : "6x"
            }),
            /*#__PURE__*/ _jsx(ProgressDots, {
                progress: currentIndex,
                total: productSpotlightCards.length
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                size: isMobile ? "3x" : "9x"
            })
        ]
    });
};
var OuterContainer = styled(Column).attrs({
    align: "center"
}).withConfig({
    componentId: "sc-2f716fcf-0"
})([
    "background-color:",
    ";",
    "{background-color:",
    ";}"
], function(props) {
    return(// eslint-disable-next-line no-nested-ternary
    props.$isPersonalized ? Core.surface.muted : props.$showCaseStudies ? Core.surface.inverse : Decorative.tint.neutral);
}, responsiveMediaQueries.mobile, function(param) {
    var $isPersonalized = param.$isPersonalized;
    return $isPersonalized ? Core.surface.muted : Decorative.tint.neutral;
});
var Container = styled(Column).withConfig({
    componentId: "sc-2f716fcf-1"
})([
    "width:100%;",
    "{min-height:360px;}"
], responsiveMediaQueries.mobile);
var CardContainer = styled.div.withConfig({
    componentId: "sc-2f716fcf-2"
})([
    "position:relative;padding:0 15px 15px 15px;",
    "{padding:0 3px;}z-index:1;"
], responsiveMediaQueries.mobile);
var SubtitleContainer = styled(Typography).attrs({
    variant: "paragraphSansRegular"
}).withConfig({
    componentId: "sc-2f716fcf-3"
})([
    "width:269px;"
]);
var CardTitle = styled(Typography).withConfig({
    componentId: "sc-2f716fcf-4"
})([
    "width:269px;"
]);
var ImageContainer = styled(Column).attrs({
    justify: "center",
    align: "center"
}).withConfig({
    componentId: "sc-2f716fcf-5"
})([
    "width:100%;height:294px;padding:36px 36px 0 36px;background-color:",
    ";",
    "{height:181px;padding:0;}"
], Core.surface.muted, responsiveMediaQueries.mobile);
var TitlesContainer = styled(Column).attrs({
    align: "center",
    justify: "center"
}).withConfig({
    componentId: "sc-2f716fcf-6"
})([
    "width:100%;background-color:white;border-top:1px solid ",
    ";",
    "{height:147px;border-radius:0px 0px 4px 4px;}",
    "{height:127px;}"
], Core.border.muted, responsiveMediaQueries.mobile, responsiveMediaQueries.tabletAndAbove);
var Card = styled(Column).withConfig({
    componentId: "sc-2f716fcf-7"
})([
    "opacity:",
    ";transition:opacity 0.3s ease-in-out;&:hover{cursor:pointer;}width:482px;background-color:",
    ";",
    "{width:100%;border:1px solid ",
    ";border-radius:4px;height:328px;}z-index:1;",
    "{height:421px;border:1px solid ",
    ";filter:",
    ";}"
], function(props) {
    return props.$isSelected ? "100%" : "30%";
}, Core.surface.muted, responsiveMediaQueries.mobile, Core.border.muted, responsiveMediaQueries.tabletAndAbove, Core.border.muted, function(props) {
    return props.$isSelected ? "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15))" : "";
});
var StyledSlider = styled(Slider).withConfig({
    componentId: "sc-2f716fcf-8"
})([
    "",
    "{height:328px;min-width:317px;}"
], responsiveMediaQueries.mobile);
var SlideControlWrapper = styled.div.withConfig({
    componentId: "sc-2f716fcf-9"
})([
    "position:relative;"
]);
var SlideControlButtonsWrapper = styled(Row).attrs({
    justify: "center",
    align: "center"
}).withConfig({
    componentId: "sc-2f716fcf-10"
})([
    "position:absolute;top:200px;z-index:2;left:50%;transform:translate(-50%,-50%);"
]);
var SlideControlButton = styled(Button).withConfig({
    componentId: "sc-2f716fcf-11"
})([
    "padding:0px;height:35px;width:35px;cursor:pointer;border-radius:50%;border:1px solid ",
    ";"
], Core.border.muted);
var Dot = styled.div.withConfig({
    componentId: "sc-2f716fcf-12"
})([
    "height:8px;width:8px;border-radius:50%;background-color:",
    ";margin:3.5px;",
    "{height:5px;width:5px;margin:2.5px;}"
], function(param) {
    var $color = param.$color;
    return $color || Core.surface.subdued;
}, responsiveMediaQueries.mobile);
var ProgressDots = function(param) {
    var progress = param.progress, total = param.total;
    return /*#__PURE__*/ _jsx(Row, {
        children: times(total, function(num) {
            return /*#__PURE__*/ _jsx(Dot, {
                color: num === progress ? Core.surface.enabled : undefined
            }, num);
        })
    });
};
export var BrandProductSpotlight = observer(BrandProductSpotlightBase);
